import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import type { Product } from '../types/product';
import type { Category } from '../services/categoryService';
import { logger } from '../utils/logger';

interface MultilingualText {
  en?: string;
  my?: string;
  [key: string]: string | undefined;
}

interface RawCategory {
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  isActive?: boolean;
  order?: number;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  _nameMultilingual?: MultilingualText;
  _descriptionMultilingual?: MultilingualText;
  description?: string;
  imageUrl?: string;
  icon?: string;
}

interface RawProduct {
  _id?: string;
  id?: string;
  name?: string;
  title?: string;
  image?: string;
  imageUrl?: string;
  isActive?: boolean;
  stock?: number;
  operator?: string;
  operatorId?:
    | string
    | {
        _id: string;
        name: {
          en: string;
          my: string;
        };
        code: string;
      };
  price: number;
  description?: string;
  _nameMultilingual?: MultilingualText;
  _descriptionMultilingual?: MultilingualText;
  categoryId?: string;
  category?: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  stockAvailable?: boolean;
  outOfStock?: boolean;
}

/**
 * Hook for normalizing multilingual data from API with proper language handling
 * @returns Functions for normalizing various multilingual data types
 */
export const useLanguageNormalizer = () => {
  const { i18n } = useTranslation();

  /**
   * Get localized text from multilingual object
   * @param multilingualObj - Object containing multilingual text (e.g., _nameMultilingual, _descriptionMultilingual)
   * @param fallbackText - Fallback text if multilingual object is not available
   * @param language - Optional language override (defaults to current i18n language)
   * @returns Localized text
   */
  const getLocalizedText = useCallback(
    (
      multilingualObj: MultilingualText | null | undefined,
      fallbackText: string = '',
      language?: string,
    ): string => {
      const currentLanguage = language || i18n.language;

      if (!multilingualObj) {
        return fallbackText;
      }

      // Map 'mm' language code to 'my' property in API response
      if ((currentLanguage === 'mm' || currentLanguage === 'my') && multilingualObj.my) {
        return multilingualObj.my;
      } else if (currentLanguage === 'en' && multilingualObj.en) {
        return multilingualObj.en;
      } else {
        // Fallback: try current language first, then other languages, then fallback text
        return (
          multilingualObj[currentLanguage] ||
          multilingualObj.en ||
          multilingualObj.my ||
          fallbackText
        );
      }
    },
    [i18n.language],
  );

  /**
   * Normalize category data from API based on current language
   * @param category - Raw category data from API
   * @param language - Optional language override (defaults to current i18n language)
   * @returns Normalized category object
   */
  const normalizeCategory = useCallback(
    (category: RawCategory, language?: string): Category => {
      const categoryName = getLocalizedText(category._nameMultilingual, category.name, language);

      return {
        ...category,
        _id: category._id || category.id || '',
        name: categoryName,
        slug: category.slug || '',
        isActive: category.isActive ?? true,
        order: category.order ?? 0,
        createdAt: category.createdAt || '',
        updatedAt: category.updatedAt || '',
        __v: category.__v ?? 0,
      } as Category;
    },
    [getLocalizedText],
  );

  /**
   * Normalize an array of categories
   * @param categories - Array of raw category data from API
   * @param language - Optional language override
   * @returns Array of normalized category objects
   */
  const normalizeCategories = useCallback(
    (categories: (RawCategory | Category)[], language?: string): Category[] => {
      return categories.map((category) => normalizeCategory(category as RawCategory, language));
    },
    [normalizeCategory],
  );

  /**
   * Normalize product data from API based on current language
   * @param product - Raw product data from API
   * @param language - Optional language override (defaults to current i18n language)
   * @returns Product with localized name and description
   */
  const normalizeProduct = useCallback(
    (product: RawProduct, language?: string): Product => {
      const productName = getLocalizedText(
        product._nameMultilingual,
        product.name || product.title,
        language,
      );
      const productDescription = getLocalizedText(
        product._descriptionMultilingual,
        product.description,
        language,
      );

      const normalizedProduct = {
        ...product,
        id: product._id || product.id || '',
        title: productName,
        name: productName,
        price: product.price,
        imageUrl: product.image || product.imageUrl || '',
        stockAvailable: product.outOfStock === false,
        operatorId: (() => {
          if (product.operatorId && typeof product.operatorId === 'object') {
            return product.operatorId;
          }
          return product.operator || product.operatorId || 'unknown';
        })(),
        description: productDescription,
      } as Product;

      logger.log('normalizeProduct - input:', product);
      logger.log('normalizeProduct - output:', normalizedProduct);
      return normalizedProduct;
    },
    [getLocalizedText],
  );

  /**
   * Normalize an array of products
   * @param products - Array of raw product data from API
   * @param language - Optional language override
   * @returns Array of products with localized names and descriptions
   */
  const normalizeProducts = useCallback(
    (products: (RawProduct | Product)[], language?: string): Product[] => {
      return products.map((product) => normalizeProduct(product as RawProduct, language));
    },
    [normalizeProduct],
  );

  return {
    getLocalizedText,
    normalizeCategory,
    normalizeCategories,
    normalizeProduct,
    normalizeProducts,
  };
};

// Backward compatibility export
export const useCategoryNormalizer = useLanguageNormalizer;
